import { PlasmicComponent, PlasmicRootProvider } from '@plasmicapp/loader-nextjs';
import NothingHereMessage from 'components/NothingHereMessage';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import {
  EecProduct,
  trackEecImpression,
  trackEecProductClick,
} from 'utils/analytics/analytics';
import { useUTMStore } from 'hooks/stores/useUTMStore';
import { observer } from 'mobx-react-lite';
import { PrizeV2 as Prize } from 'types/Prize';
import { ENTER_CTA_LINK } from 'utils/cta/CTA';
import { getEnterLinkForPrize } from 'utils/partners/getEnterLinkForPrize';
import { HeaderSection } from './prize-page/HeaderSectionPrize';
import { HowToPlay } from './prize-page/HowToPlay';
import { PrizeRecommendationSection } from './prize-page/PrizeRecommendationSection';
import { PLASMIC } from 'plasmic-init';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import styles from './PrizePage.module.css';
import RoundedButton from 'components/RoundedButton';

const discountCodeTextForSubscriberDiscountContentCode = (code: string) => {
  switch (code) {
    case 'VED':
      return '15% discount with Virgin Experience Days';

    case 'AH':
      return '10% discount with Authentic House';

    case 'INDYTUTE':
      return '£10 discount with Indytute';

    case 'URBANB':
      return '10% discount with urban B';

    case 'NATURALLY-LEGIT':
      return '10% discount with Naturally Legit';

    default:
      return null;
  }
};

const daymadeDifferences = [
  {
    emoji: "🌳",
    heading: "Every play plants a tree",
    content: <p>A tree is planted with every purchase and we <Link href="https://www.daymade.co.uk/giving-back"><a>carbon offset</a></Link> all travel prizes</p>
  },
  {
    emoji: "🫶",
    heading: "Supporting small businesses",
    content: <p>We proudly stand behind small businesses and B-Corps</p>
  },
  {
    emoji: "⭐",
    heading: "Tried & tested",
    content: <p>We’re rated Excellent on <Link href="https://www.trustpilot.com/review/daymade.co.uk"><a>Trustpilot</a></Link>  with over 2,000 5* reviews</p>
  },
] as const;

interface IPrizePageProps {
  prize: Prize;
  hideBackButton: boolean;
}

const PrizePage = observer(
  ({ prize, hideBackButton = false }: IPrizePageProps) => {
    const router = useRouter();
    const { utmParams } = useUTMStore();

    // Track product impression with GA EEC.
    useEffect(() => {
      if (!prize) return;

      (async () => {
        await trackEecImpression(prize.slug, EecProduct.LOTTERY);
      })();
    }, []);

    if (!prize) return <NothingHereMessage />;

    const {
      whatsIncludedHeading,
      whatsIncludedBody,
      type,
      products,
      subscriberDiscountContentCode,
      mainDrawWinningCriterion,
      visible,
      slug,
    } = prize;

    const trackProductClickAndGoToEnter = async () => {
      const link = getEnterLinkForPrize(slug, utmParams.utm_source);

      router.push(link ?? ENTER_CTA_LINK);

      // Track product click with GA EEC.
      await trackEecProductClick(prize.slug, EecProduct.LOTTERY);
    };

  const premiumCriteria = mainDrawWinningCriterion?.matchesRequired['PREMIUM'];
  const liteCriteria = mainDrawWinningCriterion?.matchesRequired['LITE'];
  const freemiumCriteria = mainDrawWinningCriterion?.matchesRequired['FREEMIUM'];
  
  return (
    <div className="aboutUsTypographyReset">
      <HeaderSection
        prize={prize}
        ctaOnClick={trackProductClickAndGoToEnter}
      />

      <HowToPlay
        prizeTier={mainDrawWinningCriterion?.tier}
        boostRequired={mainDrawWinningCriterion?.isBoostRequired}
        premiumCriteria={premiumCriteria}
        liteCriteria={liteCriteria}
        freemiumCriteria={freemiumCriteria}
      />
      <AboutUsModule background="grey">
        <AboutUsSiteContainer>
          <h2 className="aboutUsTypographyHeaderXlargeOnWhite" data-text="The DAYMADE difference">The DAYMADE difference</h2>
          <ul className={styles.daymadeDifference+ ' withMinimalSpacing'}>
            {daymadeDifferences.map(x => <li key={x.emoji}><span className={styles.daymadeDifferenceEmoji}>{x.emoji}</span><h3 className="aboutUsTypographyHeaderMedium">{x.heading}</h3>{x.content}</li>)}
          </ul>
        </AboutUsSiteContainer>
      </AboutUsModule>
      <AboutUsModule background="white">
        <AboutUsSiteContainer>
          <h2 className="aboutUsTypographyHeaderXlargeOnWhite" data-text="It could be you!">It could be you!</h2>
          <div
            /* Need overflow hidden, because the plasmic component spills out of its container */
            style={{overflow: 'hidden'}}
          >
            <div className={styles.winnerCarouselContainer}>
              <PlasmicRootProvider
                loader={PLASMIC}
              >
                <PlasmicComponent component="WinnerSectionCMS" componentProps={{ carouselOnly: true}} />
              </PlasmicRootProvider>
            </div>
            <p className="aboutUsTypographyCentered">
              <RoundedButton
                onClick={trackProductClickAndGoToEnter}
                rebrandDm2025
              >
                Enter to win
              </RoundedButton>
            </p>
          </div>
        </AboutUsSiteContainer>
      </AboutUsModule>
      <PrizeRecommendationSection prizeId={prize.id} />
     
    </div>
  );
});

export default PrizePage;
